import { ArrowRight, Check } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@heidi/design-system/button';
import {
  TypographyCaption,
  TypographyH3,
  TypographyP,
} from '@heidi/design-system/typography';
import type { TranslationKey } from '@heidi/runtime/i18n';
import { useTranslations } from '@heidi/runtime/i18n';
import { useOnboardingContext } from '../../context/OnboardingContext';

import type { EditActivity } from '../../learnBasicsTypes';
import { LearnBasicsAccordionRow, type RowState } from './LearnBasicsAccordionRow';
import { LearnBasicsDocumentPicker } from './LearnBasicsDocumentPicker';
import { LearnBasicsEvidenceQuestions } from './LearnBasicsEvidenceQuestions';
import { getActiveEditTasks } from './NoteEditStep.utils';
import { DetailLevelSelector } from './preview/variants/DetailLevelSelector';
import { OnboardingStepShell } from './shared/OnboardingStepShell';
import { useLearnBasicsTaskActions } from './useLearnBasicsTaskActions';

interface LearnBasicsSteppedStepProps {
  onContinue: () => void;
  onSkip: () => void;
}

type LearnBasicsStepKey = keyof EditActivity;

interface LearnBasicsStep {
  key: LearnBasicsStepKey;
  title: string;
  subtitle: string;
}

/** Stepped-flow copy keyed by edit task; order comes from `getActiveEditTasks`. */
const STEPPED_STEP_COPY: Record<
  LearnBasicsStepKey,
  { titleKey: TranslationKey; subtitleKey: TranslationKey }
> = {
  updatedStyle: {
    titleKey: 'signup.learnBasicsChooseDetail',
    subtitleKey: 'signup.learnBasicsStepDetailSubtitle',
  },
  usedEvidence: {
    titleKey: 'signup.learnBasicsStepEvidenceTitle',
    subtitleKey: 'signup.learnBasicsStepEvidenceSubtitle',
  },
  createdDocument: {
    titleKey: 'signup.learnBasicsStepDocumentTitle',
    subtitleKey: 'signup.learnBasicsStepDocumentSubtitle',
  },
};

/**
 * Stepped Learn-the-basics flow (sample-conversation flag ON), rendered as a
 * vertical accordion: the active row expands to reveal its control, completed
 * rows collapse to a tick (and can be re-opened), upcoming rows are dimmed.
 * The active control drives the live note + tabs in the preview via context.
 */
export function LearnBasicsSteppedStep({
  onContinue,
  onSkip,
}: LearnBasicsSteppedStepProps) {
  const { t, locale } = useTranslations();
  const { micTest, registerNoteEditBackHandler } = useOnboardingContext();
  const { editActivity, setNoteTab } = micTest;
  const [stepIndex, setStepIndex] = useState(0);

  const {
    evidenceQuestions,
    documentOptions,
    selectDetailLevel: handleDetailSelect,
    askEvidence: handleAskEvidence,
    toggleDocument: handleToggleDocument,
  } = useLearnBasicsTaskActions();
  const steps = useMemo<LearnBasicsStep[]>(
    () =>
      getActiveEditTasks(locale, { includeDetailLevelTask: true }).map((task) => ({
        key: task.key,
        title: t(STEPPED_STEP_COPY[task.key].titleKey),
        subtitle: t(STEPPED_STEP_COPY[task.key].subtitleKey),
      })),
    [locale, t]
  );
  const currentStep = steps[stepIndex] ?? steps[0];
  const stepComplete = currentStep ? editActivity[currentStep.key] : false;
  const isLastStep = stepIndex === steps.length - 1;
  const continueDisabled = stepIndex > 0 && !stepComplete;

  useEffect(() => {
    if (stepIndex <= steps.length - 1) return;
    setStepIndex(Math.max(steps.length - 1, 0));
  }, [stepIndex, steps.length]);

  // Let the header Back button step through the sub-steps before leaving.
  useEffect(() => {
    registerNoteEditBackHandler(() => {
      if (stepIndex > 0) {
        setNoteTab('note');
        setStepIndex((i) => i - 1);
        return true;
      }
      return false;
    });
    return () => registerNoteEditBackHandler(null);
  }, [stepIndex, registerNoteEditBackHandler, setNoteTab]);

  const advanceTo = (next: number) => {
    setNoteTab('note');
    setStepIndex(next);
  };

  const reopen = (i: number) => {
    setStepIndex(i);
    const step = steps[i];
    if (step?.key === 'usedEvidence' && micTest.evidenceRequested) {
      setNoteTab('evidence');
    } else if (step?.key === 'createdDocument' && micTest.selectedDocuments.length > 0) {
      setNoteTab(`doc-${micTest.selectedDocuments[0]!}`);
    } else {
      setNoteTab('note');
    }
  };

  const handleContinue = () => {
    if (isLastStep) {
      onContinue();
      return;
    }
    advanceTo(stepIndex + 1);
  };

  const renderControl = (i: number) => {
    const step = steps[i];
    if (step?.key === 'updatedStyle') {
      return (
        <DetailLevelSelector level={micTest.detailLevel} onSelect={handleDetailSelect} />
      );
    }
    if (step?.key === 'usedEvidence') {
      return (
        <div className="flex flex-col gap-2">
          <LearnBasicsEvidenceQuestions
            questions={evidenceQuestions}
            askedIndex={micTest.evidenceRequested ? micTest.evidenceQueryIndex : null}
            onAsk={handleAskEvidence}
          />
          {editActivity.usedEvidence && (
            <TypographyCaption className="flex items-center gap-1.5 text-accent-forest">
              <Check className="size-3.5" />
              {t('signup.learnBasicsEvidenceDone')}
            </TypographyCaption>
          )}
        </div>
      );
    }
    return (
      <LearnBasicsDocumentPicker
        options={documentOptions}
        selected={micTest.selectedDocuments}
        onToggle={handleToggleDocument}
      />
    );
  };

  return (
    <OnboardingStepShell
      contentClassName="gap-6"
      footer={
        <Button size="lg" variant="ghost" onClick={onSkip} className="w-full">
          {t('common.skip')}
        </Button>
      }
    >
      <div className="flex flex-col gap-2">
        <TypographyH3>{t('signup.learnBasicsTitle')}</TypographyH3>
        <TypographyP className="text-text-secondary">
          {t('signup.learnBasicsSubtitlePolish')}
        </TypographyP>
      </div>

      <div className="flex flex-col gap-2.5">
        {steps.map((step, i) => {
          const isActive = i === stepIndex;
          const state: RowState = isActive
            ? 'active'
            : editActivity[step.key]
              ? 'completed'
              : 'upcoming';
          return (
            <LearnBasicsAccordionRow
              key={step.title}
              index={i}
              title={step.title}
              subtitle={step.subtitle}
              state={state}
              onReopen={state === 'completed' ? () => reopen(i) : undefined}
            >
              {isActive && (
                <>
                  {renderControl(i)}
                  <div className="mt-4 flex justify-end">
                    <Button
                      size="sm"
                      onClick={handleContinue}
                      disabled={continueDisabled}
                      className="inline-flex items-center gap-1.5"
                    >
                      {isLastStep
                        ? t('signup.learnBasicsDone')
                        : t('signup.learnBasicsNextStep')}
                      {!isLastStep && <ArrowRight className="size-4 rtl:-scale-x-100" />}
                    </Button>
                  </div>
                </>
              )}
            </LearnBasicsAccordionRow>
          );
        })}
      </div>
    </OnboardingStepShell>
  );
}
